import React, { useState, useEffect, useRef } from "react";
import {
  Tabs,
  Tab,
  TextField,
  MenuItem,
  Button,
  CircularProgress,
  Select,
  FormControl,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Modal,
} from "@mui/material";
import { HttpService } from "../../service/HttpService";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import { MdClose } from "react-icons/md";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import FullScreenLoader from "../../components/Loader/FullScreenLoader";
import SapAnalysisImportModal from "./SapAnalysisImportModal";
import SapAnalysisArchiveModal from "./SapAnalysisArchiveModal";
import DeleteModal from "../../components/Model/DeleteModal";
import "./SapAnalysis.scss";
import { RxCross1 } from "react-icons/rx";
import { TbAlertHexagonFilled } from "react-icons/tb";

const SapAnalysis = ({ roomId }) => {
  const httpService = new HttpService();

  const [cultivars, setCultivars] = useState([]);
  const [cultivarTab, setCultivarTab] = useState(0);
  const [growthPhases, setGrowthPhases] = useState([]);
  const [reports, setReports] = useState([]);

  // newRows holds local rows not yet saved to DB
  const [newRows, setNewRows] = useState([]);

  const [loading, setLoading] = useState(false);
  const [viewPdfUrl, setViewPdfUrl] = useState(null);
  const [activePdfRowId, setActivePdfRowId] = useState(null);

  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importingRowInfo, setImportingRowInfo] = useState(null);
  const [importConfirmOpen, setImportConfirmOpen] = useState(false);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteRowId, setDeleteRowId] = useState(null);
  const [deleteIsNew, setDeleteIsNew] = useState(false);

  const [archiveModalOpen, setArchiveModalOpen] = useState(false);
  const [archiveRowId, setArchiveRowId] = useState(null);
  const [allPlants, setAllPlants] = useState([]);

  // Fetch initial data on mount
  useEffect(() => {
    fetchGrowthPhases();
  }, []);

  // Fetch cultivars for room when allPlants is ready
  useEffect(() => {
    if (roomId && allPlants.length > 0) {
      fetchCultivarsForRoom(roomId);
    }
  }, [roomId, allPlants]);

  const fetchGrowthPhases = async () => {
    try {
      const plantsRes = await httpService.get("plant/get_plant");
      setAllPlants(plantsRes?.data?.data || []);
      const phasesRes = await httpService.get("/growth-phase/get-growth-phase");
      setGrowthPhases(phasesRes?.data || []);
    } catch (e) {
      console.error("Error fetching initial data", e);
    }
  };

  // Fetch cultivars for room
  const fetchCultivarsForRoom = async (roomId) => {
    setLoading(true);
    try {
      const res = await httpService.get(`mapping?room_id=${roomId}`);
      const list = Array.isArray(res?.data?.data) ? res.data.data : [];

      const uniqueCultivars = [];
      const map = new Map();

      list.forEach((item) => {
        if (item.cultivar_name && !map.has(item.cultivar_name)) {
          map.set(item.cultivar_name, true);
          const matchedPlant = allPlants.find(
            (p) => p.name === item.cultivar_name,
          );

          uniqueCultivars.push({
            id: matchedPlant ? matchedPlant.id : null,
            name: item.cultivar_name,
          });
        }
      });

      if (uniqueCultivars.length === 0) {
        setCultivars([]);
      } else {
        setCultivars(uniqueCultivars.filter((c) => c.id));
      }

      setCultivarTab(0);
    } catch (e) {
      console.error("Error fetching cultivars", e);
      setCultivars([]);
    }
    setLoading(false);
  };

  // Fetch reports
  useEffect(() => {
    if (roomId && cultivars.length > 0) {
      const activeCultivar = cultivars[cultivarTab];
      if (activeCultivar) {
        fetchReports(roomId, activeCultivar.id);
        // reset new rows and hide pdf viewer when switching tabs
        setNewRows([]);
        setViewPdfUrl(null);
        setActivePdfRowId(null);
      }
    }
  }, [roomId, cultivarTab, cultivars]);

  const fetchReports = async (roomId, cultivarId) => {
    setLoading(true);
    try {
      const res = await httpService.get(
        `/sap-analysis?roomId=${roomId}&cultivarId=${cultivarId}&is_archived=${false}`,
      );
      const data = res?.data || [];
      setReports(data);

      // If no data, show at least one empty form row
      if (data.length === 0) {
        setNewRows([
          {
            id: `new-${Date.now()}`,
            growthPhaseId: "",
            testDate: "",
            isNew: true,
          },
        ]);
      } else {
        setNewRows([]);
      }
    } catch (error) {
      console.error("Error fetching sap analysis reports", error);
      setReports([]);
      setNewRows([
        {
          id: `new-${Date.now()}`,
          growthPhaseId: "",
          testDate: "",
          isNew: true,
        },
      ]);
    }
    setLoading(false);
  };

  // ----- ROW ACTIONS -----

  const handleAddRow = () => {
    const newId = `new-${Date.now()}`;
    setNewRows([
      ...newRows,
      {
        id: newId,
        growthPhaseId: "",
        testDate: "",
        isNew: true,
      },
    ]);
  };

  const handleUpdateNewRow = (id, field, value) => {
    setNewRows(
      newRows.map((row) => (row.id === id ? { ...row, [field]: value } : row)),
    );
  };

  const handleDeleteNewRow = (id) => {
    setNewRows(newRows.filter((row) => row.id !== id));
  };

  const triggerImport = (row) => {
    // If growth phase and test date are empty on a new row, warn
    if (row.isNew && (!row.growthPhaseId || !row.testDate)) {
      toast.warning(
        "Please select Growth Phase and Test Date before importing.",
      );
      return;
    }
    setImportingRowInfo(row);
    // show confirmation dialog first
    setImportConfirmOpen(true);
  };

  const handleImport = async (file) => {
    if (!file || !importingRowInfo) return;

    const activeCultivar = cultivars[cultivarTab];
    if (!roomId || !activeCultivar) return;

    setLoading(true);
    try {
      const payload = new FormData();
      payload.append("roomId", roomId);
      payload.append("cultivarId", activeCultivar.id);

      // If it's an existing record being overwritten, we use its values
      payload.append(
        "growthPhaseId",
        importingRowInfo.growthPhaseId || importingRowInfo.growth_phase_id,
      );
      payload.append(
        "testDate",
        importingRowInfo.testDate || importingRowInfo.test_date,
      );
      payload.append("file", file);

      const res = await httpService.post("sap-analysis", payload);

      if (res?.success) {
        toast.success("Sap Analysis uploaded successfully");
        // If it was overwriting an existing record, we should delete the old one
        // (Assuming backend doesn't handle PUT, we do a manual clean up if we want, or just leave it for now. Let's just delete the old one if it's existing)
        if (!importingRowInfo.isNew) {
          await httpService.delete(`sap-analysis/${importingRowInfo.id}`);
        } else {
          // Remove from newRows
          handleDeleteNewRow(importingRowInfo.id);
        }
        fetchReports(roomId, activeCultivar.id);
      } else {
        toast.error(res?.message || "Failed to upload");
      }
    } catch (error) {
      console.error("Upload error", error);
      toast.error("Upload failed");
    }
    setLoading(false);
    setImportingRowInfo(null);
  };

  const handleDeleteReport = async (id) => {
    setLoading(true);
    try {
      const res = await httpService.delete(`sap-analysis/${id}`);
      if (res?.success) {
        toast.success("Report deleted");
        setViewPdfUrl(null);
        setActivePdfRowId(null);
        const activeCultivar = cultivars[cultivarTab];
        fetchReports(roomId, activeCultivar.id);
      } else {
        toast.error("Failed to delete report");
      }
    } catch (e) {
      toast.error("Delete failed");
    }
    setLoading(false);
  };

  const handleConfirmImport = () => {
    setImportConfirmOpen(false);
    setImportModalOpen(true);
  };

  const triggerArchive = (id) => {
    setArchiveRowId(id);
    setArchiveModalOpen(true);
  };

  const handleConfirmArchive = async () => {
    if (!archiveRowId) return;
    setLoading(true);
    try {
      const res = await httpService.put(`sap-analysis/archive/${archiveRowId}`);
      if (res?.success) {
        toast.success("Report archived successfully");
        const activeCultivar = cultivars[cultivarTab];
        setViewPdfUrl(null);
        setActivePdfRowId(null);
        fetchReports(roomId, activeCultivar.id);
      } else {
        toast.error(res?.message || "Failed to archive report");
      }
    } catch (e) {
      console.error("Archive error", e);
      toast.error("Archive failed");
    }
    setLoading(false);
    setArchiveModalOpen(false);
    setArchiveRowId(null);
  };

  const triggerDelete = (id, isNew) => {
    setDeleteRowId(id);
    setDeleteIsNew(isNew);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = () => {
    if (deleteIsNew) {
      handleDeleteNewRow(deleteRowId);
    } else {
      handleDeleteReport(deleteRowId);
    }
    setDeleteModalOpen(false);
    setDeleteRowId(null);
  };

  // ----- RENDERING ROWS -----
  const renderRow = (row) => {
    const isNew = row.isNew;

    return (
      <div className="custom-row" key={row.id}>
        <div className="row-content">
          <div className="col">
            <FormControl fullWidth size="small">
              <Select
                value={isNew ? row.growthPhaseId : row.growth_phase_id}
                onChange={(e) =>
                  isNew &&
                  handleUpdateNewRow(row.id, "growthPhaseId", e.target.value)
                }
                displayEmpty
                disabled={!isNew}
                sx={{
                  "& .MuiSelect-select.Mui-disabled": {
                    WebkitTextFillColor: "#333",
                  },
                  "& fieldset": {
                    border: "none",
                  },
                }}
              >
                <MenuItem value="" disabled>
                  Growth Phase
                </MenuItem>
                {growthPhases.map((phase) => (
                  <MenuItem key={phase.id} value={phase.id}>
                    {phase.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>

          <div className="col">
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                value={
                  isNew
                    ? (row.testDate ? dayjs(row.testDate) : null)
                    : (row.test_date ? dayjs(row.test_date) : null)
                }
                onChange={(newValue) => {
                  if (isNew) {
                    const dateValue = newValue
                      ? newValue.format("YYYY-MM-DD")
                      : "";
                    handleUpdateNewRow(row.id, "testDate", dateValue);
                  }
                }}
                disabled={!isNew}
                disableFuture
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: true,
                    placeholder: "Test Date",
                    sx: {
                      "& .MuiInputBase-input.Mui-disabled": {
                        WebkitTextFillColor: "#333",
                      },
                      "& .MuiInputBase-input::placeholder": {
                        color: "#aaa",
                        opacity: 1,
                      },
                      "& fieldset": {
                        border: "none",
                      },
                    },
                  },
                }}
              />
            </LocalizationProvider>
          </div>

          <div className="col">
            <button
              className={`text-link ${isNew ? "blue" : ""}`}
              onClick={() => triggerImport(row)}
              disabled={!isNew}
            >
              Import
            </button>
          </div>

          <div className="col">
            <button
              className={`text-link ${!isNew && row.pdf_url ? "black" : ""} ${activePdfRowId === row.id ? "active-view" : ""}`}
              onClick={() => {
                setViewPdfUrl(row.pdf_url);
                setActivePdfRowId(row.id);
              }}
              disabled={isNew || !row.pdf_url}
            >
              View
            </button>
          </div>

          <div className="col">
            <button
              className={`text-link ${!isNew ? "green" : ""}`}
              onClick={() => triggerArchive(row.id)}
              disabled={isNew}
            >
              Archive
            </button>
          </div>
        </div>

        <Button
          className="delete-btn"
          onClick={() => triggerDelete(row.id, isNew)}
        >
          Delete
        </Button>
      </div>
    );
  };

  return (
    <div className="sap-analysis-container">
      {loading && <FullScreenLoader />}

      <SapAnalysisImportModal
        open={importModalOpen}
        onClose={() => setImportModalOpen(false)}
        onImport={handleImport}
      />

      <DeleteModal
        open={deleteModalOpen}
        HandleClose={() => setDeleteModalOpen(false)}
        HandleDelete={handleConfirmDelete}
        message="Are you sure you want to delete this record?"
        user={true}
      />

      <SapAnalysisArchiveModal
        open={archiveModalOpen}
        HandleClose={() => setArchiveModalOpen(false)}
        HandleArchive={handleConfirmArchive}
      />

      {/* Import Confirmation Dialog */}
      {/* <Dialog
        open={importConfirmOpen}
        onClose={() => setImportConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Import</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to import the SAP Analysis for the selected
            Growth Phase and Test Date?
          </Typography>
          <div style={{ marginTop: 12 }}>
            <Typography variant="body2">
              <strong>Growth Phase:</strong>{" "}
              {growthPhases.find(
                (p) =>
                  p.id ===
                  (importingRowInfo?.growthPhaseId ||
                    importingRowInfo?.growth_phase_id),
              )?.name ||
                importingRowInfo?.growthPhaseId ||
                importingRowInfo?.growth_phase_id ||
                "-"}
            </Typography>
            <Typography variant="body2">
              <strong>Test Date:</strong>{" "}
              {importingRowInfo?.testDate || importingRowInfo?.test_date || "-"}
            </Typography>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmImport} variant="contained">
            Sure
          </Button>
        </DialogActions>
      </Dialog> */}

      <Modal
        open={importConfirmOpen}
        onClose={() => setImportConfirmOpen(false)}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <div className="modal_block">
          <div className="modal_header">
            <div className="modal_title_wrapper">
              <h2 className="modal_title">Are you Sure ?</h2>
            </div>
            <div className="cancel-btn-wrap">
              <RxCross1 onClick={() => setImportConfirmOpen(false)} />
            </div>
          </div>
          <div className="modal_content">
            <div className="modal_content_inner">
              <div>
                <TbAlertHexagonFilled />
              </div>
              <div>
                <p>
                  Are you sure you want to import the SAP Analysis for the
                  selected Growth Phase and Test Date?
                </p>
              </div>
            </div>
          </div>
          <div className="modal_footer">
            <button
              onClick={() => setImportConfirmOpen(false)}
              className="btn btn-border"
            >
              Cancel
            </button>
            <button
              onClick={() => handleConfirmImport()}
              className="btn btn-primary"
            >
              import
            </button>
          </div>
        </div>
      </Modal>

      {/* Cultivar Tabs */}
      {cultivars.length > 0 ? (
        <div className="tabs-container">
          <Tabs
            value={cultivarTab}
            onChange={(e, nv) => setCultivarTab(nv)}
            variant="scrollable"
            scrollButtons={false}
          >
            {cultivars.map((cultivar) => (
              <Tab key={cultivar.id} label={cultivar.name} />
            ))}
          </Tabs>
        </div>
      ) : (
        <div className="tabs-container" style={{ padding: "12px 24px" }}>
          <p style={{ color: "#777", margin: 0, fontSize: "14px" }}>
            No cultivars for this room.
          </p>
        </div>
      )}

      {/* Table Layout */}
      {cultivars.length > 0 && (
        <div className="content-layout">
          <div className="table-section">
            {reports.map(renderRow)}
            {newRows.map(renderRow)}
          </div>
          <div className="add-section">
            <Button
              variant="contained"
              className="add-btn"
              onClick={handleAddRow}
              disabled={!reports.length > 0}
            >
              Add
            </Button>
          </div>
        </div>
      )}

      {/* Inline PDF Viewer */}
      {viewPdfUrl && (
        <div className="pdf-viewer-inline">
          <iframe
            src={`${viewPdfUrl}`}
            title="PDF Viewer"
            className="sap-analysis-iframe"
          />
        </div>
      )}
    </div>
  );
};

export default SapAnalysis;
